import { useState, useEffect } from "react";
import { font, Panel, Toggle, SectionLabel } from "@realtyflows/ui";
import { useTheme } from "../contexts/ThemeContext";
import useResponsive from "../hooks/useResponsive";
import {
  getAutomations, toggleAutomation as apiToggle, getAutomationStats,
  createAutomation, updateAutomation, deleteAutomation,
  getDocTemplates, updateDocTemplate, createDocTemplate, deleteDocTemplate,
  getEmailTemplates, getPipelines,
} from "../api";
import ConfirmModal from "../components/ConfirmModal";

const pipelineLabels = { buyer: "Buyer Pipeline", seller: "Seller Pipeline" };

/* ── shared style factories (must accept `t` from useTheme) ── */
const makeStyles = (t) => {
  const inputStyle = {
    width: "100%", padding: "10px 12px", borderRadius: 8, border: `1.5px solid ${t.border}`,
    background: t.surface, color: t.textPrimary, fontSize: 13, fontFamily: font.body,
    outline: "none", boxSizing: "border-box",
  };
  return {
    inputStyle,
    selectStyle: { ...inputStyle, appearance: "none", cursor: "pointer" },
    labelStyle: { fontSize: 11, fontWeight: 700, color: t.textSecondary, marginBottom: 4, fontFamily: font.mono, letterSpacing: 0.5 },
    btnPrimary: {
      background: t.emerald, color: "#fff", border: "none", borderRadius: 8,
      padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: font.display,
    },
    btnSecondary: {
      background: "transparent", color: t.textMuted, border: `1.5px solid ${t.border}`, borderRadius: 8,
      padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font.body,
    },
    btnDanger: {
      background: "transparent", color: "#ef4444", border: `1.5px solid #ef444440`, borderRadius: 8,
      padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font.body,
    },
  };
};

/* ── Three-dot menu ── */
function ThreeDotMenu({ items }) {
  const t = useTheme();
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <div onClick={e => { e.stopPropagation(); setOpen(!open); }}
        style={{ cursor: "pointer", padding: "4px 6px", borderRadius: 6, fontSize: 18, color: t.textMuted, letterSpacing: 2, lineHeight: 1, userSelect: "none" }}
        onMouseEnter={e => e.currentTarget.style.background = `${t.border}60`}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
        &#8942;
      </div>
      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 999 }} onClick={() => setOpen(false)} />
          <div style={{
            position: "absolute", right: 0, top: "100%", marginTop: 4, zIndex: 1000,
            background: t.surface, border: `1.5px solid ${t.border}`, borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)", minWidth: 170, overflow: "hidden",
          }}>
            {items.map((item, i) => (
              item.divider ? (
                <div key={i} style={{ height: 1, background: t.border, margin: "4px 0" }} />
              ) : (
                <div key={i}
                  onClick={e => { e.stopPropagation(); setOpen(false); item.onClick(); }}
                  style={{
                    padding: "10px 16px", fontSize: 13, fontFamily: font.body, cursor: "pointer",
                    color: item.danger ? "#ef4444" : t.textPrimary,
                    display: "flex", alignItems: "center", gap: 8,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = item.danger ? "#ef444410" : `${t.emerald}10`}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  {item.label}
                </div>
              )
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Modal backdrop ── */
function Modal({ children, onClose }) {
  const t = useTheme();
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)" }} />
      <div style={{ position: "relative", background: t.surface, borderRadius: 16, padding: 28, width: 480, maxHeight: "85vh", overflowY: "auto", border: `1.5px solid ${t.border}`, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
        onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export default function Automations() {
  const t = useTheme();
  const responsive = useResponsive();
  const [automations, setAutomations] = useState([]);
  const [stats, setStats] = useState({ totalRuns: 0, activeCount: 0, documentsSent: 0, documentsSigned: 0, emailsSent: 0 });
  const [signingAutomations, setSigningAutomations] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [editSigning, setEditSigning] = useState(null);   // doc template object or "new"
  const [editWorkflow, setEditWorkflow] = useState(null);  // automation object or "new"
  const [confirmRemove, setConfirmRemove] = useState(null); // { id, name } for remove confirmation
  const [saving, setSaving] = useState(false);

  const fetchData = () => {
    Promise.all([getAutomations(), getAutomationStats(), getDocTemplates(), getEmailTemplates(), getPipelines()])
      .then(([a, s, templates, eTmpls, pipes]) => {
        setAutomations(a);
        setStats(s);
        setAllTemplates(templates || []);
        const signing = (templates || [])
          .filter(tmpl => tmpl.stage && tmpl.pipeline_type)
          .map(tmpl => ({
            id: tmpl.id,
            name: tmpl.name,
            description: tmpl.description || "",
            pipeline: pipelineLabels[tmpl.pipeline_type] || tmpl.pipeline_type,
            pipelineType: tmpl.pipeline_type,
            stage: tmpl.stage,
            triggerType: tmpl.trigger_type || "native",
            autoSend: tmpl.auto_send !== false,
          }));
        setSigningAutomations(signing);
        setEmailTemplates(eTmpls || []);
        setPipelines(pipes || []);
      })
      .catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { fetchData(); }, []);

  /* ── Signing automation handlers ── */
  const handleToggleSigning = async (id, current) => {
    try { await updateDocTemplate(id, { auto_send: !current }); fetchData(); } catch (err) { console.error(err); }
  };

  const handleSaveSigning = async (form) => {
    setSaving(true);
    try {
      if (form.id) {
        await updateDocTemplate(form.id, {
          pipeline_type: form.pipelineType, stage: form.stage, auto_send: true,
        });
      } else {
        await createDocTemplate({
          name: form.name,
          pipeline_type: form.pipelineType, stage: form.stage,
          trigger_type: "native",
        });
      }
      setEditSigning(null);
      fetchData();
    } catch (err) { alert(err.message); }
    setSaving(false);
  };

  const handleDeleteSigning = (id) => {
    const sa = signingAutomations.find(s => s.id === id);
    setConfirmRemove({ id, name: sa?.name || "this automation" });
  };

  const confirmRemoveAutomation = async () => {
    if (!confirmRemove) return;
    try {
      if (confirmRemove.type === "workflow") {
        await deleteAutomation(confirmRemove.id);
        setEditWorkflow(null);
      } else {
        await updateDocTemplate(confirmRemove.id, { auto_send: false, stage: null });
        setEditSigning(null);
      }
      setConfirmRemove(null);
      fetchData();
    } catch (err) { alert(err.message); }
  };

  /* ── Custom workflow handlers ── */
  const handleToggle = async (id) => {
    try { await apiToggle(id); fetchData(); } catch (err) { console.error(err); }
  };

  const handleSaveWorkflow = async (form) => {
    setSaving(true);
    try {
      if (form.id) {
        await updateAutomation(form.id, {
          name: form.name, trigger_event: form.trigger_event,
          action: form.action, action2: form.action2 || null, delay: form.delay || null,
        });
      } else {
        await createAutomation({
          name: form.name, trigger_event: form.trigger_event,
          action: form.action, action2: form.action2 || null, delay: form.delay || null,
        });
      }
      setEditWorkflow(null);
      fetchData();
    } catch (err) { alert(err.message); }
    setSaving(false);
  };

  const handleDeleteWorkflow = (id) => {
    const a = automations.find(x => x.id === id);
    setConfirmRemove({ id, name: a?.name || "this automation", type: "workflow" });
  };

  // Get stages for a pipeline type
  const getStagesForType = (pipelineType) => {
    const pipe = pipelines.find(p => p.slug === pipelineType);
    return pipe?.stages?.map(s => s.name) || [];
  };

  if (loading) return <div style={{ color: t.textMuted, padding: 40, textAlign: "center", fontFamily: font.body }}>Loading automations...</div>;

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "1fr" : "1fr 340px", gap: 20 }}>
        <div>
          {/* ─── Document Signing Automations ─── */}
          <SectionLabel>Document Signing Automations</SectionLabel>
          <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, marginBottom: 14 }}>
            Documents auto-sent when deals reach specific pipeline stages
          </div>

          {signingAutomations.length === 0 && (
            <div style={{ color: t.textMuted, fontSize: 13, fontFamily: font.body, padding: 20, textAlign: "center" }}>
              No document signing automations configured yet.
            </div>
          )}

          {signingAutomations.map(sa => (
            <div key={sa.id} className="rf-sitem" style={{
              background: t.surface, border: `1.5px solid ${sa.autoSend ? `${t.emerald}20` : t.border}`,
              borderRadius: 12, padding: 20, marginBottom: 12, transition: "transform 0.2s, box-shadow 0.2s",
              opacity: sa.autoSend ? 1 : 0.6, position: "relative",
            }}>
              <div style={{ position: "absolute", top: 6, right: 6 }}>
                <ThreeDotMenu items={[
                  { label: "Edit Automation", onClick: () => setEditSigning(sa) },
                  { label: sa.autoSend ? "Disable" : "Enable", onClick: () => handleToggleSigning(sa.id, sa.autoSend) },
                  { divider: true },
                  { label: "Remove Automation", danger: true, onClick: () => handleDeleteSigning(sa.id) },
                ]} />
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, paddingRight: 28 }}>
                <div style={{ flex: 1, cursor: "pointer" }} onClick={() => setEditSigning(sa)}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>{sa.name}</div>
                  {sa.description && <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginTop: 4 }}>{sa.description}</div>}
                </div>
                <Toggle checked={sa.autoSend} onChange={() => handleToggleSigning(sa.id, sa.autoSend)} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ background: t.amberLight, border: `1px solid ${t.amber}40`, borderRadius: 8, padding: "6px 12px" }}>
                  <div style={{ fontSize: 9, color: t.amber, fontWeight: 700, letterSpacing: 1, marginBottom: 2, fontFamily: font.mono }}>TRIGGER</div>
                  <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>Deal enters "{sa.stage}"</div>
                </div>
                <div style={{ color: t.borderDark, fontSize: 20 }}>&rarr;</div>
                <div style={{ background: `${t.sky}10`, border: `1px solid ${t.sky}40`, borderRadius: 8, padding: "6px 12px" }}>
                  <div style={{ fontSize: 9, color: t.sky, fontWeight: 700, letterSpacing: 1, marginBottom: 2, fontFamily: font.mono }}>PIPELINE</div>
                  <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>{sa.pipeline}</div>
                </div>
                <div style={{ color: t.borderDark, fontSize: 20 }}>&rarr;</div>
                <div style={{ background: t.emeraldLight, border: `1px solid ${t.emerald}40`, borderRadius: 8, padding: "6px 12px" }}>
                  <div style={{ fontSize: 9, color: t.emerald, fontWeight: 700, letterSpacing: 1, marginBottom: 2, fontFamily: font.mono }}>ACTION</div>
                  <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>Send for e-signing</div>
                </div>
              </div>
            </div>
          ))}

          {/* Add new signing automation */}
          <div style={{ background: t.surface, border: `2px dashed ${t.border}`, borderRadius: 12, padding: 16, textAlign: "center", cursor: "pointer", transition: "border-color 0.2s", marginBottom: 24 }}
            onMouseEnter={e => e.currentTarget.style.borderColor = `${t.emerald}80`}
            onMouseLeave={e => e.currentTarget.style.borderColor = t.border}
            onClick={() => setEditSigning({ id: null, name: "", description: "", pipelineType: "buyer", stage: "" })}
          >
            <div style={{ fontSize: 20, color: t.borderDark, marginBottom: 4 }}>+</div>
            <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>Add Signing Automation</div>
          </div>

          {/* ─── Custom Workflow Automations ─── */}
          <SectionLabel>Custom Workflows</SectionLabel>
          <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, marginBottom: 14 }}>
            Email and follow-up automations triggered by events
          </div>

          {automations.map(a => (
            <div key={a.id} className="rf-sitem" style={{
              background: t.surface, border: `1.5px solid ${a.active ? `${t.emerald}20` : t.border}`,
              borderRadius: 12, padding: 20, marginBottom: 12, transition: "transform 0.2s, box-shadow 0.2s",
              opacity: a.active ? 1 : 0.6, position: "relative",
            }}>
              <div style={{ position: "absolute", top: 6, right: 6 }}>
                <ThreeDotMenu items={[
                  { label: "Edit Workflow", onClick: () => setEditWorkflow(a) },
                  { label: a.active ? "Disable" : "Enable", onClick: () => handleToggle(a.id) },
                  { divider: true },
                  { label: "Delete Workflow", danger: true, onClick: () => handleDeleteWorkflow(a.id) },
                ]} />
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, paddingRight: 28 }}>
                <div style={{ flex: 1, cursor: "pointer" }} onClick={() => setEditWorkflow(a)}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>{a.name}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono, marginTop: 4 }}>{a.runs} runs total</div>
                </div>
                <Toggle checked={a.active} onChange={() => handleToggle(a.id)} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ background: t.amberLight, border: `1px solid ${t.amber}40`, borderRadius: 8, padding: "6px 12px" }}>
                  <div style={{ fontSize: 9, color: t.amber, fontWeight: 700, letterSpacing: 1, marginBottom: 2, fontFamily: font.mono }}>TRIGGER</div>
                  <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>{a.trigger_event}</div>
                </div>
                <div style={{ color: t.borderDark, fontSize: 20 }}>&rarr;</div>
                <div style={{ background: t.emeraldLight, border: `1px solid ${t.emerald}40`, borderRadius: 8, padding: "6px 12px" }}>
                  <div style={{ fontSize: 9, color: t.emerald, fontWeight: 700, letterSpacing: 1, marginBottom: 2, fontFamily: font.mono }}>ACTION</div>
                  <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>{a.action}</div>
                </div>
                {a.delay && <>
                  <div style={{ color: t.borderDark, fontSize: 20 }}>&rarr;</div>
                  <div style={{ background: `${t.sky}10`, border: `1px solid ${t.sky}40`, borderRadius: 8, padding: "6px 12px" }}>
                    <div style={{ fontSize: 9, color: t.sky, fontWeight: 700, letterSpacing: 1, marginBottom: 2, fontFamily: font.mono }}>DELAY</div>
                    <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>{a.delay}</div>
                  </div>
                </>}
                {a.action2 && <>
                  <div style={{ color: t.borderDark, fontSize: 20 }}>&rarr;</div>
                  <div style={{ background: `${t.purple}10`, border: `1px solid ${t.purple}40`, borderRadius: 8, padding: "6px 12px" }}>
                    <div style={{ fontSize: 9, color: t.purple, fontWeight: 700, letterSpacing: 1, marginBottom: 2, fontFamily: font.mono }}>THEN</div>
                    <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>{a.action2}</div>
                  </div>
                </>}
              </div>
            </div>
          ))}

          <div style={{ background: t.surface, border: `2px dashed ${t.border}`, borderRadius: 12, padding: 16, textAlign: "center", cursor: "pointer", transition: "border-color 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = `${t.emerald}80`}
            onMouseLeave={e => e.currentTarget.style.borderColor = t.border}
            onClick={() => setEditWorkflow({ id: null, name: "", trigger_event: "", action: "", action2: "", delay: "" })}
          >
            <div style={{ fontSize: 20, color: t.borderDark, marginBottom: 4 }}>+</div>
            <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>Add Custom Workflow</div>
          </div>
        </div>

        {/* ─── Sidebar ─── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Panel padding={18}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>Automation Stats</div>
            {[
              { label: "Documents Sent", value: stats.documentsSent?.toString() || "0", color: t.emerald },
              { label: "Documents Signed", value: stats.documentsSigned?.toString() || "0", color: t.emeraldDeep },
              { label: "Emails Sent", value: stats.emailsSent?.toString() || "0", color: t.amber },
              { label: "Workflow Runs", value: stats.totalRuns?.toString() || "0", color: t.sky },
              { label: "Active Workflows", value: stats.activeCount?.toString() || "0", color: t.purple },
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${t.border}`, fontSize: 12 }}>
                <span style={{ color: t.textSecondary, fontFamily: font.body }}>{s.label}</span>
                <span style={{ color: s.color, fontFamily: font.mono, fontWeight: 700 }}>{s.value}</span>
              </div>
            ))}
          </Panel>

          <Panel padding={18}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 12, fontFamily: font.display }}>Email Templates</div>
            {emailTemplates.length === 0 && (
              <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, textAlign: "center", padding: 12 }}>No email templates yet</div>
            )}
            {emailTemplates.slice(0, 5).map(tp => (
              <div key={tp.id} style={{ background: t.surfaceWarm, borderRadius: 8, padding: 12, border: `1px solid ${t.border}`, marginBottom: 8 }}>
                <div style={{ fontSize: 9, color: t.textMuted, marginBottom: 6, letterSpacing: 1, fontWeight: 700, fontFamily: font.mono }}>{(tp.name || "TEMPLATE").toUpperCase()}</div>
                <div style={{ fontSize: 12, color: t.textPrimary, lineHeight: 1.6, fontFamily: font.body }}>
                  {tp.subject || tp.body?.slice(0, 120) || "No content"}
                </div>
              </div>
            ))}
          </Panel>

          <Panel padding={18}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 12, fontFamily: font.display }}>Signing Pipeline Map</div>
            {["buyer", "seller"].map(type => {
              const items = signingAutomations.filter(sa => sa.pipelineType === type && sa.autoSend);
              if (items.length === 0) return null;
              return (
                <div key={type} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, marginBottom: 6, fontFamily: font.mono }}>
                    {type.toUpperCase()} PIPELINE
                  </div>
                  {items.map(sa => (
                    <div key={sa.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <div style={{ width: 6, height: 6, borderRadius: 3, background: t.emerald, flexShrink: 0 }} />
                      <div style={{ fontSize: 11, color: t.textSecondary, fontFamily: font.body }}>
                        <span style={{ color: t.textPrimary, fontWeight: 600 }}>{sa.stage}</span>
                        {" → "}
                        <span style={{ color: t.emerald }}>{sa.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </Panel>
        </div>
      </div>

      {/* ═══ Edit / Create Signing Automation Modal ═══ */}
      {editSigning && (
        <SigningModal
          item={editSigning}
          pipelines={pipelines}
          allTemplates={allTemplates}
          getStagesForType={getStagesForType}
          saving={saving}
          onSave={handleSaveSigning}
          onDelete={editSigning.id ? () => handleDeleteSigning(editSigning.id) : null}
          onClose={() => setEditSigning(null)}
        />
      )}

      {/* ═══ Edit / Create Workflow Automation Modal ═══ */}
      {editWorkflow && (
        <WorkflowModal
          item={editWorkflow}
          pipelines={pipelines}
          saving={saving}
          onSave={handleSaveWorkflow}
          onDelete={editWorkflow.id ? () => handleDeleteWorkflow(editWorkflow.id) : null}
          onClose={() => setEditWorkflow(null)}
        />
      )}

      {/* ═══ Remove / Delete Confirmation ═══ */}
      {confirmRemove && (
        <ConfirmModal
          title={confirmRemove.type === "workflow" ? "Delete Workflow" : "Remove Automation"}
          message={confirmRemove.type === "workflow"
            ? `Delete "${confirmRemove.name}"? This cannot be undone.`
            : `Remove "${confirmRemove.name}"? The document template will be kept but won't auto-trigger.`}
          confirmLabel={confirmRemove.type === "workflow" ? "Delete" : "Remove"}
          onConfirm={confirmRemoveAutomation}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   Signing Automation Modal
   ═══════════════════════════════════════════ */
function SigningModal({ item, pipelines, allTemplates, getStagesForType, saving, onSave, onDelete, onClose }) {
  const t = useTheme();
  const { inputStyle, selectStyle, labelStyle, btnPrimary, btnSecondary, btnDanger } = makeStyles(t);
  const [form, setForm] = useState({
    id: item.id || null,
    name: item.name || "",
    description: item.description || "",
    pipelineType: item.pipelineType || "buyer",
    stage: item.stage || "",
  });
  const [useCustomName, setUseCustomName] = useState(false);

  const stages = getStagesForType(form.pipelineType);
  const isNew = !item.id; // based on original item, not form (form.id changes when selecting existing template)

  // Existing templates that don't already have a stage+pipeline automation assigned
  const availableTemplates = (allTemplates || []).filter(tmpl => {
    if (!tmpl.name) return false;
    // When editing, include the current template
    if (form.id && tmpl.id === form.id) return true;
    return true;
  });

  const handleSelectTemplate = (templateId) => {
    if (templateId === "__custom__") {
      setUseCustomName(true);
      setForm({ ...form, name: "" });
      return;
    }
    const tmpl = allTemplates.find(t2 => String(t2.id) === String(templateId));
    if (tmpl) {
      setUseCustomName(false);
      setForm({ ...form, name: tmpl.name, id: tmpl.id });
    }
  };

  return (
    <Modal onClose={onClose}>
      <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 20, fontFamily: font.display }}>
        {isNew ? "New Signing Automation" : "Edit Signing Automation"}
      </div>

      {isNew && !useCustomName ? (
        <div style={{ marginBottom: 16 }}>
          <div style={labelStyle}>SELECT DOCUMENT</div>
          <select style={selectStyle} value={form.id || ""}
            onChange={e => handleSelectTemplate(e.target.value)}>
            <option value="">Choose an existing document...</option>
            {availableTemplates.map(tmpl => (
              <option key={tmpl.id} value={tmpl.id}>{tmpl.name}</option>
            ))}
            <option value="__custom__">+ Create new document...</option>
          </select>
          {form.name && (
            <div style={{ fontSize: 11, color: t.emerald, marginTop: 6, fontFamily: font.body }}>
              Selected: {form.name}
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={labelStyle}>DOCUMENT NAME</div>
            {isNew && <span style={{ fontSize: 10, color: t.sky, cursor: "pointer", fontFamily: font.body }}
              onClick={() => { setUseCustomName(false); setForm({ ...form, id: null, name: "" }); }}>
              Select existing
            </span>}
          </div>
          <input style={inputStyle} value={form.name} placeholder="e.g. Buyer Representation Agreement"
            onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <div>
          <div style={labelStyle}>PIPELINE</div>
          <select style={selectStyle} value={form.pipelineType}
            onChange={e => setForm({ ...form, pipelineType: e.target.value, stage: "" })}>
            {pipelines.map(p => (
              <option key={p.slug} value={p.slug}>{p.name}</option>
            ))}
            {pipelines.length === 0 && <>
              <option value="buyer">Buyer Pipeline</option>
              <option value="seller">Seller Pipeline</option>
            </>}
          </select>
        </div>
        <div>
          <div style={labelStyle}>TRIGGER STAGE</div>
          <select style={selectStyle} value={form.stage}
            onChange={e => setForm({ ...form, stage: e.target.value })}>
            <option value="">Select stage...</option>
            {stages.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Preview */}
      {form.stage && (
        <div style={{ background: t.surfaceWarm, borderRadius: 10, padding: 14, border: `1px solid ${t.border}`, marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, marginBottom: 8, fontFamily: font.mono }}>PREVIEW</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ background: t.amberLight, color: t.amber, fontSize: 11, padding: "4px 10px", borderRadius: 6, fontWeight: 600, fontFamily: font.body }}>
              Deal enters "{form.stage}"
            </span>
            <span style={{ color: t.borderDark }}>&rarr;</span>
            <span style={{ background: t.emeraldLight, color: t.emerald, fontSize: 11, padding: "4px 10px", borderRadius: 6, fontWeight: 600, fontFamily: font.body }}>
              Send "{form.name || "..."}" for e-signing
            </span>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          {onDelete && <button style={btnDanger} onClick={onDelete}>Delete</button>}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={{ ...btnPrimary, opacity: saving || !form.name || !form.stage ? 0.5 : 1 }}
            disabled={saving || !form.name || !form.stage}
            onClick={() => onSave(form)}>
            {saving ? "Saving..." : isNew ? "Create" : "Save Changes"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ═══════════════════════════════════════════
   Workflow Automation Modal
   ═══════════════════════════════════════════ */
const triggerOptions = [
  { value: "new_client", label: "New client added" },
  { value: "deal_stage_change", label: "Deal stage changed" },
  { value: "appointment_booked", label: "Appointment booked" },
  { value: "appointment_missed", label: "Appointment missed / no-show" },
  { value: "email_received", label: "Email received from client" },
  { value: "no_activity_7d", label: "No activity for 7 days" },
  { value: "no_activity_30d", label: "No activity for 30 days" },
  { value: "deal_closed", label: "Deal closed" },
  { value: "contract_signed", label: "Contract signed" },
  { value: "contract_sent", label: "Contract sent" },
];

const actionOptions = [
  { value: "send_email", label: "Send email" },
  { value: "send_follow_up", label: "Send follow-up email" },
  { value: "create_task", label: "Create task" },
  { value: "update_status", label: "Update client status" },
  { value: "notify_agent", label: "Notify agent" },
  { value: "log_activity", label: "Log activity" },
  { value: "assign_client", label: "Assign to team member" },
];

const delayOptions = [
  { value: "", label: "Immediately" },
  { value: "5m", label: "5 minutes" },
  { value: "30m", label: "30 minutes" },
  { value: "1h", label: "1 hour" },
  { value: "4h", label: "4 hours" },
  { value: "1d", label: "1 day" },
  { value: "3d", label: "3 days" },
  { value: "7d", label: "7 days" },
];

function WorkflowModal({ item, saving, onSave, onDelete, onClose }) {
  const t = useTheme();
  const { inputStyle, selectStyle, labelStyle, btnPrimary, btnSecondary, btnDanger } = makeStyles(t);
  const [form, setForm] = useState({
    id: item.id || null,
    name: item.name || "",
    trigger_event: item.trigger_event || "",
    action: item.action || "",
    action2: item.action2 || "",
    delay: item.delay || "",
  });

  const isNew = !form.id;

  return (
    <Modal onClose={onClose}>
      <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 20, fontFamily: font.display }}>
        {isNew ? "New Workflow Automation" : "Edit Workflow Automation"}
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={labelStyle}>AUTOMATION NAME</div>
        <input style={inputStyle} value={form.name} placeholder="e.g. Welcome new buyer"
          onChange={e => setForm({ ...form, name: e.target.value })} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={labelStyle}>TRIGGER EVENT</div>
        <select style={selectStyle} value={form.trigger_event}
          onChange={e => setForm({ ...form, trigger_event: e.target.value })}>
          <option value="">Select trigger...</option>
          {triggerOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <div>
          <div style={labelStyle}>ACTION</div>
          <select style={selectStyle} value={form.action}
            onChange={e => setForm({ ...form, action: e.target.value })}>
            <option value="">Select action...</option>
            {actionOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <div style={labelStyle}>DELAY</div>
          <select style={selectStyle} value={form.delay}
            onChange={e => setForm({ ...form, delay: e.target.value })}>
            {delayOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={labelStyle}>THEN ALSO (optional)</div>
        <select style={selectStyle} value={form.action2}
          onChange={e => setForm({ ...form, action2: e.target.value })}>
          <option value="">None</option>
          {actionOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Preview */}
      {form.trigger_event && form.action && (
        <div style={{ background: t.surfaceWarm, borderRadius: 10, padding: 14, border: `1px solid ${t.border}`, marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, marginBottom: 8, fontFamily: font.mono }}>PREVIEW</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ background: t.amberLight, color: t.amber, fontSize: 11, padding: "4px 10px", borderRadius: 6, fontWeight: 600, fontFamily: font.body }}>
              {triggerOptions.find(o => o.value === form.trigger_event)?.label || form.trigger_event}
            </span>
            <span style={{ color: t.borderDark }}>&rarr;</span>
            {form.delay && <>
              <span style={{ background: `${t.sky}10`, color: t.sky, fontSize: 11, padding: "4px 10px", borderRadius: 6, fontWeight: 600, fontFamily: font.body }}>
                Wait {delayOptions.find(o => o.value === form.delay)?.label || form.delay}
              </span>
              <span style={{ color: t.borderDark }}>&rarr;</span>
            </>}
            <span style={{ background: t.emeraldLight, color: t.emerald, fontSize: 11, padding: "4px 10px", borderRadius: 6, fontWeight: 600, fontFamily: font.body }}>
              {actionOptions.find(o => o.value === form.action)?.label || form.action}
            </span>
            {form.action2 && <>
              <span style={{ color: t.borderDark }}>&rarr;</span>
              <span style={{ background: `${t.purple}10`, color: t.purple, fontSize: 11, padding: "4px 10px", borderRadius: 6, fontWeight: 600, fontFamily: font.body }}>
                {actionOptions.find(o => o.value === form.action2)?.label || form.action2}
              </span>
            </>}
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          {onDelete && <button style={btnDanger} onClick={onDelete}>Delete</button>}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={{ ...btnPrimary, opacity: saving || !form.name || !form.trigger_event || !form.action ? 0.5 : 1 }}
            disabled={saving || !form.name || !form.trigger_event || !form.action}
            onClick={() => onSave(form)}>
            {saving ? "Saving..." : isNew ? "Create" : "Save Changes"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
